import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  LocalShipping as ShippingIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

// Mock data for the revenue chart
const revenueData = [
  { month: 'Jan', revenue: 1250000 },
  { month: 'Feb', revenue: 1850000 },
  { month: 'Mar', revenue: 1717750 }, // Sum of all transactions this month
  { month: 'Apr', revenue: 0 },
  { month: 'May', revenue: 0 },
  { month: 'Jun', revenue: 0 },
];

// Mock recent transactions
const recentTransactions = [
  {
    id: 'TRX001',
    date: new Date(2024, 2, 15),
    description: 'Carbon Ceramic Brake System - 488 Pista',
    amount: 212750,
    status: 'paid',
    supplier: 'Ferrari South Africa',
  },
  {
    id: 'TRX002',
    date: new Date(2024, 2, 14),
    description: 'Engine Control Unit - 765LT',
    amount: 245000,
    status: 'pending',
    supplier: 'McLaren South Africa',
  },
  {
    id: 'TRX003',
    date: new Date(2024, 2, 13),
    description: 'Aerodynamic Package - 911 GT3 RS',
    amount: 385000,
    status: 'paid',
    supplier: 'Porsche Centre SA',
  },
  {
    id: 'TRX004',
    date: new Date(2024, 2, 12),
    description: 'Titanium Exhaust System - Aventador SVJ',
    amount: 875000,
    status: 'overdue',
    supplier: 'Lamborghini Johannesburg',
  },
];

export const DealershipDashboard: React.FC = () => {
  const formatCurrency = (amount: number) => {
    return `R ${amount.toLocaleString()}`;
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-ZA', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const calculateTotalAmount = (status?: string) => {
    return recentTransactions
      .filter(t => !status || t.status === status)
      .reduce((sum, t) => sum + t.amount, 0);
  };

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Dealership Dashboard
        </Typography>

        <Grid container spacing={3}>
          {/* Summary Cards */}
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="text.secondary" noWrap>
                  Monthly Revenue
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {formatCurrency(calculateTotalAmount())}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  March 2024
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="success.main" noWrap>
                  Completed Transactions
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {formatCurrency(calculateTotalAmount('paid'))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {recentTransactions.filter(t => t.status === 'paid').length} transactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="warning.main" noWrap>
                  Pending Payments
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {formatCurrency(calculateTotalAmount('pending'))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {recentTransactions.filter(t => t.status === 'pending').length} transactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card sx={{ minWidth: 240 }}>
              <CardContent>
                <Typography variant="h6" color="error.main" noWrap>
                  Overdue Payments
                </Typography>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    wordBreak: 'break-word'
                  }}
                >
                  {formatCurrency(calculateTotalAmount('overdue'))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {recentTransactions.filter(t => t.status === 'overdue').length} transactions
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Revenue Chart */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Revenue Trend
              </Typography>
              <Box sx={{ height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis
                      tickFormatter={(value) => `R ${(value / 1000000).toFixed(1)}M`}
                    />
                    <Tooltip
                      formatter={(value) => formatCurrency(Number(value))}
                      labelFormatter={(label) => `Month: ${label}`}
                    />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#1976d2"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          {/* Recent Transactions */}
          <Grid item xs={12}>
            <Paper>
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Recent Transactions
                </Typography>
              </Box>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Supplier</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentTransactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>{formatDate(transaction.date)}</TableCell>
                        <TableCell>{transaction.description}</TableCell>
                        <TableCell>{transaction.supplier}</TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(transaction.amount)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={transaction.status.toUpperCase()}
                            color={getStatusColor(transaction.status)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </DashboardLayout>
  );
}; 